import * as React from "react"
import { Link, createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  Building2,
  Heart,
  MailQuestion,
  RotateCcw,
  SearchCheck,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { MemberShell } from "@/components/member-shell"
import { PropertyCard } from "@/components/property-display"
import { getDashboardFn, resetMemberDemoDataFn } from "@/lib/app.functions"
import { requireMemberRoute } from "@/lib/route-guards"

export const Route = createFileRoute("/dashboard")({
  loader: async () => {
    await requireMemberRoute("/dashboard")
    return getDashboardFn()
  },
  head: () => ({
    meta: [{ title: "Member workspace · Harbor & Pine Realty" }],
  }),
  component: DashboardPage,
})

function DashboardPage() {
  const { user, summary, recentListings, recentInquiries } =
    Route.useLoaderData()
  const reset = useServerFn(resetMemberDemoDataFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)

  const handleReset = async () => {
    setPending(true)
    try {
      await reset()
      await router.invalidate()
      toast.success("Saved work, alerts, and new demo inquiries were reset.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to reset.")
    } finally {
      setPending(false)
    }
  }

  const stats = [
    [Heart, summary.favoriteCount, "Saved properties", "/favorites"],
    [
      SearchCheck,
      summary.savedSearchCount,
      "Saved searches",
      "/saved-searches",
    ],
    [Building2, summary.listingCount, "Owner listings", "/my-listings"],
    [MailQuestion, summary.inquiryCount, "Inquiries", "/account"],
  ] as const

  return (
    <MemberShell
      user={user}
      eyebrow="Overview · live D1 workspace"
      title="Your workspace"
      intro="Everything saved through the member journey returns here: shortlisted properties, saved searches, alert criteria, owner listings, and inquiry history."
      action={
        <Button
          variant="outline"
          className="field-button"
          onClick={handleReset}
          disabled={pending}
        >
          <RotateCcw />
          {pending ? "Resetting…" : "Reset saved demo work"}
        </Button>
      }
    >
      <div className="grid gap-px border bg-border sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(([Icon, value, label, to]) => (
          <Link
            key={label}
            to={to}
            className="group bg-white p-5 hover:bg-secondary"
          >
            <div className="flex items-start justify-between">
              <Icon className="size-5 text-primary" />
              <span className="font-heading text-5xl font-bold">{value}</span>
            </div>
            <p className="field-label mt-8 group-hover:text-primary">{label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-9 grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
        <section>
          <div className="flex items-end justify-between border-b-2 border-foreground pb-3">
            <h2 className="font-heading text-3xl uppercase">Recent listings</h2>
            <Link to="/my-listings" className="field-link text-sm">
              Manage all
            </Link>
          </div>
          <div className="mt-4 space-y-4">
            {recentListings.length ? (
              recentListings.map((property) => (
                <PropertyCard key={property.id} property={property} signedIn />
              ))
            ) : (
              <EmptyState copy="No owner listings yet." to="/my-listings/new" />
            )}
          </div>
        </section>
        <section>
          <div className="flex items-end justify-between border-b-2 border-foreground pb-3">
            <h2 className="font-heading text-3xl uppercase">Inquiry trail</h2>
            <span className="field-label">Latest four</span>
          </div>
          <div className="mt-4 divide-y border bg-white">
            {recentInquiries.length ? (
              recentInquiries.map((inquiry) => (
                <article key={inquiry.id} className="p-4">
                  <div className="flex justify-between gap-3">
                    <p className="text-sm font-semibold">
                      {inquiry.propertyTitle ?? "General inquiry"}
                    </p>
                    <span className="field-label text-primary">
                      {inquiry.status}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs leading-5 text-muted-foreground">
                    {inquiry.message}
                  </p>
                  {inquiry.response ? (
                    <p className="mt-3 border-l-2 border-accent pl-3 text-xs leading-5">
                      Reply: {inquiry.response}
                    </p>
                  ) : null}
                </article>
              ))
            ) : (
              <p className="p-5 text-sm text-muted-foreground">
                No inquiry trail yet.
              </p>
            )}
          </div>
        </section>
      </div>
    </MemberShell>
  )
}

function EmptyState({ copy, to }: { copy: string; to: "/my-listings/new" }) {
  return (
    <div className="border border-dashed bg-white p-6">
      <p className="text-sm text-muted-foreground">{copy}</p>
      <Link
        to={to}
        className="field-link mt-3 inline-block text-sm font-semibold"
      >
        Create a fictional listing
      </Link>
    </div>
  )
}
